import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { updateUser } from "../../redux/users/usersSlice";
import {
  ONBOARDING_STATUS,
  buildOnboardingChecklist,
  clearOnboardingSubmitted,
  getVisitedSections,
} from "../../utils/providerOnboarding";
import classes from "./ProviderOnboardingDashboard.module.css";

const pillClassForStatus = (status) => {
  if (status === ONBOARDING_STATUS.COMPLETED) return classes.pillCompleted;
  if (status === ONBOARDING_STATUS.IN_PROGRESS) return classes.pillInProgress;
  return classes.pillNotStarted;
};

const ProviderOnboardingDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);

  const [provider, setProvider] = useState(null);
  const [hasAvailabilitySlots, setHasAvailabilitySlots] = useState(false);
  const [visited, setVisited] = useState(() =>
    getVisitedSections(user?.providerId),
  );
  const [loading, setLoading] = useState(true);
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState(null);

  const providerId = user?.providerId;

  const loadOnboardingData = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      setError("Provider profile not found.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = user?.token;
      const [providerRes, availabilityRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/specialist/provider/${providerId}`,
          token
            ? { headers: { Authorization: `Bearer ${token}` } }
            : undefined,
        ),
        axios
          .get(
            `${process.env.REACT_APP_BASE_URL}/availability/${providerId}?timezone=${
              encodeURIComponent(
                Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
              )
            }`,
            token
              ? { headers: { Authorization: `Bearer ${token}` } }
              : undefined,
          )
          .catch(() => null),
      ]);

      if (providerRes.data?.success) {
        setProvider(providerRes.data.data);
      } else {
        setError(providerRes.data?.message || "Failed to load onboarding data.");
      }

      const slots = Array.isArray(availabilityRes?.data?.data)
        ? availabilityRes.data.data
        : [];
      setHasAvailabilitySlots(slots.length > 0);
      setVisited(getVisitedSections(providerId));
    } catch (err) {
      console.error("Failed to load provider onboarding:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load onboarding checklist. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [providerId, user?.token]);

  useEffect(() => {
    loadOnboardingData();
  }, [loadOnboardingData]);

  useEffect(() => {
    const onFocus = () => {
      loadOnboardingData();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOnboardingData]);

  const checklist = useMemo(
    () =>
      buildOnboardingChecklist({
        provider,
        hasAvailabilitySlots,
        visited,
      }),
    [provider, hasAvailabilitySlots, visited],
  );

  const handleOpenStep = (step) => {
    if (!step?.path) return;
    navigate(step.path);
  };

  const handleContinue = async () => {
    if (!checklist.requiredComplete || !provider || !providerId || continuing) {
      return;
    }

    setContinuing(true);
    try {
      const token = user?.token;
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : undefined;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/complete-onboarding/${providerId}`,
        {},
        headers ? { headers } : undefined,
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Failed to complete onboarding.",
        );
      }

      const nextStatus = response.data?.data?.status || "Active";

      clearOnboardingSubmitted();
      localStorage.setItem("isFirstLogin", "false");
      dispatch(
        updateUser({
          ...user,
          providerStatus: nextStatus,
          is_first_login: false,
        }),
      );
      toast.success("Welcome to your provider dashboard.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Failed to complete onboarding:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to continue to your dashboard. Please try again.",
      );
    } finally {
      setContinuing(false);
    }
  };

  if (loading) {
    return <div className={classes.loading}>Loading onboarding checklist...</div>;
  }

  if (error) {
    return <div className={classes.error}>{error}</div>;
  }

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <h1 className={classes.title}>Provider Onboarding</h1>
        <p className={classes.subtitle}>
          Complete the steps below to set up your therapist account and access
          your provider dashboard.
        </p>
      </div>

      <h2 className={classes.sectionTitle}>Onboarding Checklist</h2>

      <div className={classes.list}>
        {checklist.steps.map((step) => {
          const isContinueStep = step.id === "continue";
          const canContinue = step.canContinue && !continuing;

          return (
            <div className={classes.card} key={step.id}>
              <div className={classes.stepBadge} aria-hidden="true">
                <span className={classes.stepNumber}>{step.step}</span>
              </div>

              <div className={classes.content}>
                <p className={classes.stepEyebrow}>Step {step.step}</p>
                <h3 className={classes.cardTitle}>{step.title}</h3>
                <p className={classes.cardDescription}>{step.description}</p>
              </div>

              <div
                className={`${classes.actions} ${
                  isContinueStep ? classes.submitActions : ""
                }`}
              >
                {!isContinueStep && (
                  <span
                    className={`${classes.pill} ${pillClassForStatus(step.status)}`}
                  >
                    {step.status}
                  </span>
                )}

                {isContinueStep ? (
                  <button
                    type="button"
                    className={classes.actionBtn}
                    disabled={!canContinue}
                    onClick={handleContinue}
                  >
                    {continuing ? "Continuing..." : step.actionLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={classes.actionBtn}
                    onClick={() => handleOpenStep(step)}
                  >
                    {step.actionLabel}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProviderOnboardingDashboard;
